import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useMutation } from '@apollo/client';
import _ from 'lodash';

import { ErrorHandler, ConfirmDialog as ModalDelete } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';

import GroupsTable from './groups-table';
import ModalFolder from './modal-folder';
import ModalFolderGroup from './modal-folder-group';

import useFolders from '../../../core/hooks/use-folders';

import folderType from '../../../core/constants/folderType';

import {
  DELETE_FOLDER,
  DELETE_FOLDER_GROUP,
} from '../../../core/graphql/folders';

import {
  Container,
  HorizontalContent,
  ButtonGroup,
  Text,
  Link,
  ContainerText,
  Loader,
} from './styles';

const ManageFolders = (props) => {
  const {
    companyId,
  } = props;

  const LEARN_MORE_LINK = 'http://help.playbookux.com/en/articles/4867868-folders';

  const [editGroup, setEditGroup] = useState({});
  const [edit, setEdit] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('');
  const [isGrouModalpOpen, setIsGrouModalpOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);

  const {
    groupedFolders = [],
    loading,
    error,
    refetch,
  } = useFolders(companyId);

  const [
    deleteFolder,
    {
      loading: loadingDeleteFolder,
      error: errorDeleteFolder,
    },
  ] = useMutation(DELETE_FOLDER);

  const [
    deleteFolderGroup,
    {
      loading: loadingDeleteFolderGroup,
      error: errorDeleteFolderGroup,
    },
  ] = useMutation(DELETE_FOLDER_GROUP);

  const handleOpenGroupModal = (group) => {
    setEditGroup(group);
    setIsGrouModalpOpen(true);
  };
  const handleCloseGroupModal = () => setIsGrouModalpOpen(false);

  const handleOpenCreateModal = (group) => {
    setSelectedGroup(group);
    setIsModalOpen(true);
  };
  const handleOpenEditModal = (group, current) => {
    setSelectedGroup(group);
    setEdit(current);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenModalDelete = (id, type) => {
    setDeleteType({ id, type });
    setModalDeleteOpen(true);
  };

  const handleCloseModalDelete = () => {
    setModalDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteType && deleteType.type === folderType.FOLDER_GROUP) {
        await deleteFolderGroup(
          {
            variables: {
              id: deleteType.id,
            },
          },
        );
      }

      if (deleteType && deleteType.type === folderType.FOLDER) {
        await deleteFolder(
          {
            variables: {
              id: deleteType.id,
            },
          },
        );
      }
      refetch();
      handleCloseModalDelete();
    } catch (err) {
      const resuError = _.get(err, 'networkError.result.error.message',
        'Something went wrong while deleting');
      // eslint-disable-next-line no-console
      console.log(resuError);
    }
  };

  if (error) {
    return (
      <Container center>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving folders."
        />
      </Container>
    );
  }

  return (
    <>
      <Container>
        <HorizontalContent>
          <ContainerText>
            <Text>Manage Folders</Text>
            <Link
              href={LEARN_MORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about folders
            </Link>
          </ContainerText>

          {loading && <Loader data-loader="manage-folders-loader" />}

          <ButtonGroup
            onClick={() => handleOpenGroupModal({})}
          >
            Create Folder Group
          </ButtonGroup>
        </HorizontalContent>

        <GroupsTable
          onCreateClick={(group) => handleOpenCreateModal(group)}
          currentCompany={companyId}
          grouped={groupedFolders}
          handleOpenModalDelete={handleOpenModalDelete}
          handleOpenGroupModal={(row) => handleOpenGroupModal(row)}
          handleOpenEditModal={(group, current) => handleOpenEditModal(group, current)}
        />

      </Container>

      {isGrouModalpOpen && (
        <ModalFolderGroup
          editGroup={editGroup}
          onClose={handleCloseGroupModal}
          companyId={companyId}
        />
      )}

      {isModalOpen && (
        <ModalFolder
          companyId={companyId}
          defaultGroup={selectedGroup}
          edit={edit}
          onClose={handleCloseModal}
          groups={groupedFolders}
          setEdit={setEdit}
        />
      )}

      {isModalDeleteOpen && (
        <ModalDelete
          show
          title="Delete folder"
          warning="Warning: This cannot be undone"
          confirmText="Yes, delete folder"
          cancelText="Cancel, keep folder"
          handleClose={handleCloseModalDelete}
          handleConfirm={handleConfirmDelete}
          errorMessage={(errorDeleteFolder || errorDeleteFolderGroup) && 'Something went wrong while deleting'}
          loading={loadingDeleteFolder || loadingDeleteFolderGroup}
        />
      )}

    </>
  );
};

ManageFolders.propTypes = {
  companyId: PropTypes.string.isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  withLayout({ selectedTab: null }),
  connect(mapStateToProps, null),
);

export default enhance(ManageFolders);
